import React, { useMemo } from 'react';
import { Typography, Tag } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import CrudPage from '../../../components/common/CrudPage';
import userEventService from '../../../services/cms/userEventServiceRequest';
import eventCategoryService from '../../../services/cms/eventCategoryServiceRequest';
import eventTypeService from '../../../services/cms/eventTypeServiceRequest';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { label: 'Active', value: 1 },
  { label: 'Inactive', value: 0 },
];

const QuanLySuKienNguoiDung = () => {
  // Fetch danh mục sự kiện (dùng getList vì listActive bị CORS trên staging)
  const { data: categoriesData } = useQuery({
    queryKey: ['allEventCategories'],
    queryFn: () => eventCategoryService.getList({ page: 1, limit: 1000 }),
  });

  // Fetch thể loại sự kiện
  const { data: typesData } = useQuery({
    queryKey: ['allEventTypes'],
    queryFn: () => eventTypeService.getList({ page: 1, limit: 1000 }),
  });

  const categoriesList = useMemo(() => categoriesData?.data?.data || categoriesData?.data || [], [categoriesData]);
  const typesList = useMemo(() => typesData?.data?.data || typesData?.data || [], [typesData]);

  // Build map id → name
  const categoryMap = useMemo(() => {
    const map = {};
    categoriesList.forEach((item) => { map[item.id] = item.title || item.name; });
    return map;
  }, [categoriesList]);

  const typeMap = useMemo(() => {
    const map = {};
    typesList.forEach((item) => { map[item.id] = item.name; });
    return map;
  }, [typesList]);

  // Options cho dropdown
  const categoryOptions = useMemo(() => {
    return categoriesList.map((item) => ({
      label: `[${item.id}] ${item.title || item.name}`,
      value: item.id,
    }));
  }, [categoriesList]);

  const typeOptions = useMemo(() => {
    return typesList.map((item) => ({
      label: `[${item.id}] ${item.name}`,
      value: item.id,
    }));
  }, [typesList]);

  const filterFields = useMemo(() => [
    {
      label: 'Danh mục',
      value: 'category_id',
      options: [{ label: 'Tất cả danh mục', value: '' }, ...categoryOptions],
    },
    {
      label: 'Thể loại',
      value: 'type_id',
      options: [{ label: 'Tất cả thể loại', value: '' }, ...typeOptions],
    },
    {
      label: 'Trạng thái',
      value: 'status',
      options: [
        { label: 'Tất cả trạng thái', value: '' },
        { label: 'Duyệt', value: '1' },
        { label: 'Chưa duyệt', value: '0' },
      ],
    },
    { label: 'Tác giả', value: 'author', type: 'input' },
    { label: 'Từ ngày', value: 'start_from', type: 'date' },
    { label: 'Đến ngày', value: 'start_to', type: 'date' },
    // Giống CMS cũ: lọc theo ngày/tháng (không năm), placeholder để nguyên "By Date"
    { label: 'By Date', value: 'by_date', type: 'date', format: 'DD/MM' },
  ], [categoryOptions, typeOptions]);

  const formFields = [
    { name: 'title', label: 'Tiêu đề', type: 'input', rules: [{ required: true, message: 'Vui lòng nhập tiêu đề!' }], placeholder: 'Tiêu đề sự kiện' },
    { name: 'content', label: 'Nội dung', type: 'textarea', placeholder: 'Nội dung sự kiện', propsInput: { rows: 4 } },
    {
      name: 'category_id',
      label: 'Danh mục',
      type: 'select',
      options: categoryOptions,
      placeholder: 'Chọn danh mục',
      propsInput: { showSearch: true, allowClear: true, filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) },
    },
    {
      name: 'type_id',
      label: 'Thể loại',
      type: 'select',
      options: typeOptions,
      placeholder: 'Chọn thể loại',
      propsInput: { showSearch: true, allowClear: true, filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) },
    },
    // Giống CMS cũ: vừa upload được vừa dán được link có sẵn (điền cả hai thì link thắng)
    { name: 'image_url', label: 'Ảnh', type: 'upload-cdn', placeholder: 'URL ảnh', enterLink: true, linkPlaceholder: 'Link ảnh' },
    { name: 'location', label: 'Địa điểm', type: 'input', placeholder: 'Địa điểm' },
    { name: 'start_time', label: 'Thời gian bắt đầu', type: 'input', placeholder: 'VD: 2025-01-01 08:00:00' },
    { name: 'end_time', label: 'Thời gian kết thúc', type: 'input', placeholder: 'VD: 2025-01-01 18:00:00' },
    { name: 'link', label: 'Link', type: 'input', placeholder: 'Deep link' },
    { name: 'tags', label: 'Tags', type: 'input', placeholder: 'Tags (phân cách bởi dấu phẩy)' },
    { name: 'metadata', label: 'Metadata (JSON)', type: 'textarea', placeholder: '{"key": "value"}', propsInput: { rows: 2 } },
    { name: 'status', label: 'Trạng thái', type: 'select', options: STATUS_OPTIONS, defaultValue: 1 },
  ];

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'TIÊU ĐỀ', dataIndex: 'title', key: 'title', render: (text) => <Text strong>{text}</Text> },
    { title: 'ẢNH', dataIndex: 'image_url', key: 'image_url', width: 80, render: (url) => url ? <img src={url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : '-' },
    { title: 'DANH MỤC', dataIndex: 'category_id', key: 'category_id', width: 150, render: (val, r) => r.category_name ? `[${val}] ${r.category_name}` : (val || '-') },
    { title: 'THỂ LOẠI', dataIndex: 'type_id', key: 'type_id', width: 150, render: (val, r) => r.type_name ? `[${val}] ${r.type_name}` : (val || '-') },
    { title: 'NGƯỜI TẠO', dataIndex: 'author_name', key: 'author_name', width: 140, render: (val, r) => val || r.create_by || r.created_by || '-' },
    { title: 'TRẠNG THÁI', dataIndex: 'status', key: 'status', width: 100, render: (status) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? 'ACTIVE' : 'INACTIVE'}</Tag> },
    { title: 'NGÀY TẠO', dataIndex: 'create_time', key: 'create_time', width: 140, render: (time) => (time ? dayjs(time).format('DD/MM/YYYY HH:mm') : '-') },
  ];

  return (
    <CrudPage
      title="QUẢN LÝ SỰ KIỆN NGƯỜI DÙNG"
      subTitle="Quản lý các sự kiện do người dùng tạo"
      itemsName="Sự kiện"
      queryKey="userEventList"
      service={userEventService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm kiếm theo tiêu đề..."
      addButtonLabel="Thêm sự kiện"
      formConfig={{ width: 900 }}
    />
  );
};

export default QuanLySuKienNguoiDung;
